import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Calendar as CalIcon, Trash2 } from "lucide-react";
import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { requireSchoolAdminOrOnboard, useSchoolContext } from "@/lib/school-context";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type EventRow = Database["public"]["Tables"]["school_events"]["Row"];
const TYPES: Database["public"]["Enums"]["event_type"][] = ["academic","exam","holiday","meeting","sport","cultural","other"];

export const Route = createFileRoute("/admin/calendar")({
  ssr: false,
  beforeLoad: () => requireSchoolAdminOrOnboard(),
  head: () => ({ meta: [{ title: "Calendar — MAKAI" }] }),
  component: CalendarPage,
});

function CalendarPage() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<EventRow[]>([]);
  const [filter, setFilter] = useState<"all" | "upcoming" | "past">("upcoming");

  const load = useCallback(async () => {
    if (!active) return;
    let qb = supabase.from("school_events").select("*").eq("school_id", active.school_id).order("start_at");
    if (filter === "upcoming") qb = qb.gte("start_at", new Date().toISOString());
    if (filter === "past") qb = qb.lt("start_at", new Date().toISOString());
    const { data } = await qb;
    setRows((data ?? []) as EventRow[]);
  }, [active, filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <AdminShell title="School calendar" subtitle="Term openings, exams, holidays, sports days and staff meetings." action={<EventDialog onSaved={load} />}>
      <div className="mb-4 flex gap-2">
        {(["upcoming","all","past"] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={"rounded-full border px-3.5 py-1.5 text-xs font-semibold capitalize " + (filter === f ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground")}>{f}</button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No events. Click <b>Add event</b> to create one.</div>
      ) : (
        <ul className="space-y-3">
          {rows.map((e) => (
            <li key={e.id} className="flex items-start gap-4 rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-primary-soft text-primary">
                <CalIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate font-display text-base font-bold">{e.title}</h3>
                  <Badge variant="outline" className="capitalize">{e.type}</Badge>
                  {e.all_day && <Badge variant="secondary">All day</Badge>}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {new Date(e.start_at).toLocaleString()}{e.end_at && ` → ${new Date(e.end_at).toLocaleString()}`}
                </div>
                {e.location && <div className="text-xs text-muted-foreground">📍 {e.location}</div>}
                {e.description && <p className="mt-1 text-sm text-muted-foreground">{e.description}</p>}
              </div>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => {
                if (!confirm("Delete event?")) return;
                const { error } = await supabase.from("school_events").delete().eq("id", e.id);
                if (error) toast.error(error.message); else load();
              }}><Trash2 className="h-3.5 w-3.5" /></Button>
            </li>
          ))}
        </ul>
      )}
    </AdminShell>
  );
}

function EventDialog({ onSaved }: { onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<{ title: string; description: string; type: Database["public"]["Enums"]["event_type"]; start_at: string; end_at: string; all_day: boolean; location: string }>({
    title: "", description: "", type: "academic", start_at: "", end_at: "", all_day: false, location: "",
  });
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active || !form.title || !form.start_at) { toast.error("Title and start required"); return; }
    setBusy(true);
    const { error } = await supabase.from("school_events").insert({
      school_id: active.school_id, title: form.title, description: form.description || null,
      type: form.type, start_at: new Date(form.start_at).toISOString(),
      end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
      all_day: form.all_day, location: form.location || null,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Event added"); setOpen(false); onSaved();
    setForm({ title: "", description: "", type: "academic", start_at: "", end_at: "", all_day: false, location: "" });
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90"><Plus className="mr-1.5 h-4 w-4" /> Add event</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New event</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <Field label="Title *"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></Field>
          <Field label="Type">
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v as typeof form.type })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start *"><Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} required /></Field>
            <Field label="End"><Input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} /></Field>
          </div>
          <Field label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
          <Field label="Description"><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <div className="flex items-center justify-between rounded-lg border border-border p-3"><Label>All-day event</Label><Switch checked={form.all_day} onCheckedChange={(v) => setForm({ ...form, all_day: v })} /></div>
          <DialogFooter><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (<div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>);
}
